import { useEffect } from "react";
import { toast } from "react-toastify";
import { useImmer } from "use-immer";

import type { AgenticLogicRulePublishJobDto } from "@intelligems/ig-types/dtos/agentic-logic/publish";
import type { AgenticLogicRuleDto } from "@intelligems/ig-types/dtos/agentic-logic/rules";
import type { CopyAgenticLogicRuleRequestBodyType } from "@intelligems/ig-types/functions/agentic-logic/rules/copy";

import { useCopyAgenticLogicRule } from "@src/api/agentic-logic/copy-agentic-logic-rule";
import { useDiscardAgenticLogicRuleDraft } from "@src/api/agentic-logic/discard-agentic-logic-rule-draft";
import { useDeleteAgenticLogicRule } from "@src/api/agentic-logic/agentic-logic-rule-lifecycle";
import { useSelector } from "@src/store";
import { getAtlasCreatedRuleId } from "@src/store/slices/global";
import { useUnsavedWorkspaceGuard } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/useUnsavedWorkspaceGuard";
import { DiscardChangesDialog } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/DiscardChangesDialog";
import { useAgenticLogicSurface } from "@src/features/ai/pages/agentic-logic/surface/useAgenticLogicSurface";
import { useSurfaceNavigate } from "@src/features/ai/pages/agentic-logic/surface/useSurfaceNavigate";
import { usePublishAtlasRuleEditing } from "@src/features/ai/pages/agentic-logic/workspace/usePublishAtlasRuleEditing";

import type { RuleListUsage } from "../rules/ruleListRows";
import { RuleApplyChangesDialog } from "./RuleApplyChangesDialog";
import { RuleDraftSaveStatus } from "./RuleDraftSaveStatus";
import { RuleKeepDraftDialog } from "./RuleKeepDraftDialog";
import { RuleLeaveUnappliedDialog } from "./RuleLeaveUnappliedDialog";
import { offersKeepDraftOnLeave } from "./ruleExitMode";
import {
  type RuleMoreAction,
  RuleSaveApplyActions,
} from "./RuleSaveApplyActions";
import {
  ruleLeaveAction,
  type RuleOperationPhase,
  ruleSaveApplyState,
  saveAsNewDisabledHint,
  WRITING_HINT,
} from "./ruleSaveApplyState";
import { useAutosave } from "./useAutosave";
import { useOpenedWithUnappliedDraft } from "./useOpenedWithUnappliedDraft";
import { usePublishAnnouncement } from "./usePublishAnnouncement";
import { useSavedAgo } from "./useSavedAgo";
import type { useUnpublishedRuleChanges } from "./useUnpublishedRuleChanges";

export interface RuleSaveApplyFlowInput {
  rule: AgenticLogicRuleDto;
  ruleName: string;
  usedIn: RuleListUsage[] | null;
  comparison: ReturnType<typeof useUnpublishedRuleChanges>;
  /** The page's local edits and how to persist them. */
  edits: {
    isDirty: boolean;
    isSaving: boolean;
    canSave: boolean;
    /** Whether saving is possible after a temporary block clears. */
    canSaveOnLeave: boolean;
    saveBlockedHint: string | undefined;
    /** Changes with every edit, restarting the autosave wait. */
    revision: readonly unknown[];
    save: (onError: () => void) => void;
    savedAtTs: number | null;
  };
  phase: RuleOperationPhase;
  /** Publishes the saved draft. */
  apply: () => void;
  publishJob: AgenticLogicRulePublishJobDto | null | undefined;
  /** The code workspace to fork; template rules copy from the rule row. */
  copyDraft: CopyAgenticLogicRuleRequestBodyType["draft"] | undefined;
  copyDisabledHint: string | undefined;
  runChecks: { disabledHint: string | undefined; run: () => void } | null;
}

/**
 * The rule page's save and apply behavior, shared by code and template rules:
 * edits autosave to a draft, and what the header, the exit and the dialogs do
 * with that draft depends on whether any experience uses the rule. See
 * {@link ruleSaveApplyState}.
 */
export const useRuleSaveApplyFlow = ({
  rule,
  ruleName,
  usedIn,
  comparison,
  edits,
  phase,
  apply,
  publishJob,
  copyDraft,
  copyDisabledHint,
  runChecks,
}: RuleSaveApplyFlowInput) => {
  const navigate = useSurfaceNavigate();
  const surface = useAgenticLogicSurface();
  const discard = useDiscardAgenticLogicRuleDraft();
  const copy = useCopyAgenticLogicRule();
  const deleteRule = useDeleteAgenticLogicRule();
  const createdRuleId = useSelector(getAtlasCreatedRuleId);
  const [dialog, setDialog] = useImmer<
    "apply" | "leave" | "unsaved" | "keep" | null
  >(null);
  const [leaveAfterSave, setLeaveAfterSave] = useImmer(false);
  const [localEditsDiscarded, setLocalEditsDiscarded] = useImmer(false);

  const hasSavedChanges = comparison.hasUnpublishedChanges;
  const openedWithUnappliedDraft = useOpenedWithUnappliedDraft({
    settled: !comparison.isChecking && !comparison.comparisonFailed,
    hasSavedChanges,
  });
  const savedAgo = useSavedAgo(edits.savedAtTs);
  const autosave = useAutosave({
    pending: edits.isDirty && !localEditsDiscarded,
    canSave: edits.canSave,
    isSaving: edits.isSaving,
    revision: edits.revision,
    save: edits.save,
  });
  const state = ruleSaveApplyState({
    ruleState: rule.state,
    usedInCount: usedIn?.length ?? null,
    hasAppliedVersion: comparison.activeVersion !== null,
    hasSavedChanges,
    isDirty: edits.isDirty && !localEditsDiscarded,
    isSaving: edits.isSaving && !localEditsDiscarded,
    isComparing: comparison.isChecking,
    comparisonFailed: comparison.comparisonFailed,
    phase,
    openedWithUnappliedDraft,
    savedAgo,
    saveBlockedHint: autosave.failed
      ? "Your changes could not be saved. Edit the rule to try again."
      : edits.saveBlockedHint,
  });
  const { isWriting } = state;
  const isBusy = discard.isPending || copy.isPending;

  // Every exit is intercepted while an edit is still being written (so it is
  // flushed first) or while a used rule holds unapplied edits (so the merchant
  // is asked). A tab close only needs the first: the draft is already saved.
  const guard = useUnsavedWorkspaceGuard(state.confirmOnLeave || isWriting, {
    guardUnload: isWriting,
  });
  usePublishAtlasRuleEditing(state.confirmOnLeave || isWriting);

  const leave = () => {
    setDialog(null);
    setLeaveAfterSave(false);
    if (guard.isBlocked) {
      guard.proceed();
      return;
    }
    guard.allowNextNavigation();
    navigate(surface.exitPath);
  };
  const stay = () => {
    setDialog(null);
    setLeaveAfterSave(false);
    setLocalEditsDiscarded(false);
    guard.reset();
  };

  const requestLeave = () => {
    switch (
      ruleLeaveAction({
        confirmOnLeave: state.confirmOnLeave,
        isWriting,
        canSave: edits.canSaveOnLeave && !autosave.failed,
      })
    ) {
      case "confirm-unsaved":
        setDialog("unsaved");
        return;
      case "save-first":
        autosave.flush();
        setLeaveAfterSave(true);
        return;
      case "confirm-unapplied":
        setDialog("leave");
        return;
      case "leave":
        leave();
    }
  };

  // Back on a rule this chat just created, before anything uses it: keep the
  // draft, or take it back out (`offersKeepDraftOnLeave`). Done is the author
  // saying they are finished, so it never asks.
  const goBack = () => {
    if (
      !state.confirmOnLeave &&
      offersKeepDraftOnLeave({
        ruleId: rule.id,
        createdRuleId,
        usedIn,
      })
    ) {
      setDialog("keep");
      return;
    }
    requestLeave();
  };

  // Leave first, then delete: the delete drops this rule's cached read, and a
  // workspace still mounted on it re-renders without its rule and routes away
  // on its own — to the wrong surface. Its promise outlives the unmount, so
  // the toast still lands on the list; a failure toasts from the hook.
  const deleteAndLeave = () => {
    leave();
    void deleteRule
      .mutateAsync({
        pathParams: { ruleId: rule.id },
        queryParams: undefined,
        body: undefined,
      })
      .then(() => toast.info(`${ruleName} deleted`))
      .catch(() => undefined);
  };

  // A route change the blocker caught gets the same treatment as Back.
  useEffect(() => {
    if (guard.isBlocked && dialog === null && !leaveAfterSave) {
      requestLeave();
    }
  });
  // An exit that waited for its edits to be written resumes once they are.
  useEffect(() => {
    if (!leaveAfterSave) {
      return;
    }
    if (autosave.failed || (isWriting && !edits.canSaveOnLeave)) {
      setLeaveAfterSave(false);
      setDialog("unsaved");
      return;
    }
    if (isWriting) {
      return;
    }
    if (state.confirmOnLeave) {
      setLeaveAfterSave(false);
      setDialog("leave");
    } else {
      leave();
    }
  });

  usePublishAnnouncement({
    publishJob,
    activeVersion: comparison.activeVersion,
    usedInCount: usedIn?.length ?? null,
  });

  const runDiscard = async () => {
    await discard.mutateAsync(rule.id);
    toast.info("Changes discarded · the live version is unchanged");
  };

  /**
   * The edits become a rule of their own, used nowhere, and this rule goes
   * back to its live version. The copy opens next — in an upsell builder that
   * is what makes it the upsell's pending rule.
   */
  const saveAsNew = async () => {
    const name = `${ruleName} (copy)`;
    const result = await copy.mutateAsync({
      pathParams: { ruleId: rule.id },
      queryParams: undefined,
      body: {
        name,
        description: rule.description.trim() || name,
        ...(copyDraft && !localEditsDiscarded ? { draft: copyDraft } : {}),
        ...(comparison.activeVersion !== null && hasSavedChanges
          ? { discardSourceDraft: true }
          : {}),
      },
    });
    toast.success(`Saved as a new rule · ${name}. ${ruleName} is unchanged.`);
    setDialog(null);
    setLeaveAfterSave(false);
    guard.reset();
    guard.allowNextNavigation();
    navigate(surface.rulePath(result.rule.id));
  };

  const saveAsNewHint = saveAsNewDisabledHint({
    isWriting,
    isBusy,
    copyDisabledHint,
  });
  const saveAsNewDisabled = saveAsNewHint !== undefined;
  const moreActions: RuleMoreAction[] = [
    {
      label: "Save as new rule",
      disabledHint: saveAsNewHint,
      onSelect: () => void saveAsNew().catch(() => undefined),
    },
    ...(runChecks
      ? [
          {
            label: "Run checks",
            disabledHint: isWriting ? WRITING_HINT : runChecks.disabledHint,
            onSelect: runChecks.run,
          },
        ]
      : []),
  ];

  return {
    state,
    goBack,
    /** For the back-link row. */
    status: <RuleDraftSaveStatus status={state.status} />,
    actions: (
      <RuleSaveApplyActions
        state={state}
        isDiscarding={discard.isPending}
        onDone={requestLeave}
        onDiscard={() => void runDiscard().catch(() => undefined)}
        onApply={() => {
          if (state.applyDisabledHint === undefined) {
            setDialog("apply");
          }
        }}
        moreActions={moreActions}
      />
    ),
    /** Starts an apply without the confirm, for an entry point that already confirmed. */
    applyNow: apply,
    dialogs: (
      <>
        <DiscardChangesDialog
          open={dialog === "unsaved"}
          entityLabel="rule"
          entityName={ruleName}
          onKeepEditing={stay}
          onDiscard={() => {
            setLocalEditsDiscarded(true);
            if (hasSavedChanges) {
              setDialog("leave");
            } else {
              leave();
            }
          }}
        />
        <RuleApplyChangesDialog
          open={dialog === "apply"}
          experiences={usedIn ?? []}
          isApplying={state.isApplying}
          disabledHint={state.applyDisabledHint}
          saveAsNewDisabled={saveAsNewDisabled}
          onClose={() => setDialog(null)}
          onSaveAsNew={() => void saveAsNew().catch(() => undefined)}
          onApply={() => {
            setDialog(null);
            apply();
          }}
        />
        <RuleKeepDraftDialog
          open={dialog === "keep"}
          ruleName={ruleName}
          isDeleting={deleteRule.isPending}
          onClose={stay}
          onDelete={deleteAndLeave}
          onKeep={() => {
            setDialog(null);
            requestLeave();
          }}
        />
        <RuleLeaveUnappliedDialog
          open={dialog === "leave"}
          usedInCount={usedIn?.length ?? 0}
          isBusy={isBusy}
          applyDisabledHint={state.applyDisabledHint}
          saveAsNewDisabled={saveAsNewDisabled}
          onClose={stay}
          onSaveAsNew={() => void saveAsNew().catch(() => undefined)}
          onDiscard={() =>
            void runDiscard()
              .then(leave)
              .catch(() => undefined)
          }
          onApply={() => {
            apply();
            leave();
          }}
        />
      </>
    ),
  };
};
